import { useEffect, useState } from 'react';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Key, Loader2, X } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { SecretDisplayDialog } from './SecretDisplayDialog';

interface CreateOAuthClientDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onCreated: () => void;
    /** API base path: '/oauth-clients' for platform, '/applications/{appKey}/oauth-clients' for app-level */
    apiPath: string;
    title?: string;
    subtitle?: string;
}

interface OAuthClientCredentials {
    id: string;
    client_id: string;
    client_secret: string;
    name: string;
    description: string;
    scopes: string[];
    environment: string;
    status: string;
    created: string;
}

const SCOPE_OPTIONS = [
    { value: 'registry:read', label: 'Registry Read' },
    { value: 'registry:admin', label: 'Registry Admin' },
    { value: 'mcp:invoke', label: 'MCP Invoke' },
    { value: 'capability:invoke', label: 'Capability Invoke' },
];

export function CreateOAuthClientDrawer({
    isOpen,
    onClose,
    onCreated,
    apiPath,
    title = 'Create OAuth Client',
    subtitle = 'Create a new OAuth 2.0 client for machine-to-machine access.',
}: CreateOAuthClientDrawerProps) {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [environment, setEnvironment] = useState('production');
    const [selectedScopes, setSelectedScopes] = useState<string[]>(['registry:read']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [credentials, setCredentials] = useState<OAuthClientCredentials | null>(null);

    useEffect(() => {
        if (isOpen) {
            setName('');
            setDescription('');
            setEnvironment('production');
            setSelectedScopes(['registry:read']);
            setError(null);
            setCredentials(null);
            setLoading(false);
        }
    }, [isOpen]);

    const handleSubmit = async () => {
        setError(null);
        if (!name.trim()) {
            setError('Name is required');
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post<OAuthClientCredentials>(apiPath, {
                name: name.trim(),
                description: description.trim(),
                scopes: selectedScopes,
                environment,
            });
            setCredentials(data);
        } catch (err: unknown) {
            const msg =
                (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
                (err as { message?: string })?.message ??
                'Failed to create OAuth client';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const handleScopeToggle = (scope: string) => {
        setSelectedScopes((prev) =>
            prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope],
        );
    };

    const handleSecretDialogClose = () => {
        setCredentials(null);
        onClose();
        onCreated();
    };

    const inputClassName =
        'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

    return (
        <>
            {credentials && (
                <SecretDisplayDialog
                    open={!!credentials}
                    onClose={handleSecretDialogClose}
                    clientId={credentials.client_id}
                    clientSecret={credentials.client_secret}
                    name={credentials.name}
                />
            )}
            <SlideDrawer isOpen={isOpen && !credentials} onClose={onClose}>
                                <div className="relative border-b border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-8">
                                    <div className="flex items-center justify-between">
                                        <div className="flex min-w-0 items-center gap-3">
                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100">
                                                <Key className="h-5 w-5 text-indigo-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
                                                <p className="text-sm text-slate-500">{subtitle}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="shrink-0 rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto bg-white p-6 sm:p-8">
                                    <div className="mx-auto max-w-2xl space-y-6">
                                        <div className="space-y-2">
                                            <Label htmlFor="oauth-client-name">
                                                Name <span className="text-red-500">*</span>
                                            </Label>
                                            <Input
                                                id="oauth-client-name"
                                                placeholder="e.g. Production Backend"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                className={inputClassName}
                                                autoFocus
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label htmlFor="oauth-client-description">Description</Label>
                                            <textarea
                                                id="oauth-client-description"
                                                placeholder="What this client is used for..."
                                                value={description}
                                                onChange={(e) => setDescription(e.target.value)}
                                                rows={3}
                                                className={`${inputClassName} resize-none`}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Environment</Label>
                                            <Select value={environment} onValueChange={setEnvironment}>
                                                <SelectTrigger className="w-48">
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="production">Production</SelectItem>
                                                    <SelectItem value="staging">Staging</SelectItem>
                                                    <SelectItem value="development">Development</SelectItem>
                                                    <SelectItem value="test">Test</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Scopes</Label>
                                            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-4">
                                                {SCOPE_OPTIONS.map((opt) => (
                                                    <label
                                                        key={opt.value}
                                                        className="flex cursor-pointer items-center gap-3"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedScopes.includes(opt.value)}
                                                            onChange={() => handleScopeToggle(opt.value)}
                                                            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                        />
                                                        <span className="text-sm text-slate-700">
                                                            <code className="rounded bg-white px-1.5 py-0.5 text-xs">
                                                                {opt.value}
                                                            </code>{' '}
                                                            <span className="text-slate-500">— {opt.label}</span>
                                                        </span>
                                                    </label>
                                                ))}
                                            </div>
                                        </div>

                                        {error && <p className="text-sm text-red-600">{error}</p>}
                                    </div>
                                </div>

                                <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-6 py-4 sm:px-8">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={loading}
                                        className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm transition-all hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => void handleSubmit()}
                                        disabled={loading || !name.trim()}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>
                                                <Key size={16} />
                                                Create Client
                                            </>
                                        )}
                                    </button>
                                </div>
            </SlideDrawer>
        </>
    );
}
